import { Action } from 'redux'
import { combineEpics, Epic, ofType } from 'redux-observable'
import { catchError, EMPTY, filter, map, mergeMap, of, switchMap } from 'rxjs'
import {
  ApplyTempCheckedOrders,
  ResetCheckedOrdersAction,
  SubmitCheckedOrdersAction
} from '../checkedOrders/actions'
import { getCheckedOrders } from '../checkedOrders/selectors'
import {
  CancelOrderAction,
  cancelOrders,
  CancelOrdersAction,
  CreateOrderAction,
  updateOrdersValidations,
  updateOrderValidation
} from '../order/actions'
import { orderModelToParams } from '../order/helpers'
import { getOrderById, getOrderBySecurityAndType } from '../order/selectors'
import { logError } from '../ws/actions'
import { StagedOrder } from './types'

import {
  addOrUpdateStagedOrders,
  AddOrUpdateStagedOrdersAction,
  RemoveStagedOrderAction,
  removeStagedOrders,
  RemoveStagedOrdersAction,
  setFocusOnOrder
} from './actions'

const clearOrderValidationOnRemovedStagedOrder: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('stagedOrders.removeStagedOrder'),
    map((action: RemoveStagedOrderAction) =>
      updateOrderValidation({
        securityId: action.payload.securityId,
        orderType: action.payload.orderType,
        error: undefined
      })
    )
  )

const clearOrderValidationOnRemovedStagedOrders: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('stagedOrders.removeStagedOrders'),
    map((action: RemoveStagedOrdersAction) => {
      const payload = action.payload
      const validations = payload.map((result) => {
        return {
          securityId: result.securityId,
          orderType: result.orderType,
          error: undefined
        }
      })
      return updateOrdersValidations(validations)
    })
  )

const updateStagedOrdersOnCreateOrderEpic: Epic<Action> = (action$) =>
  action$.pipe(
    ofType('order.createOrder'),
    map((action: CreateOrderAction) => {
      const {
        actualPrice: price,
        spread,
        isSpreadOrder,
        clientSize: size,
        individualMin,
        tob,
        totalSize
      } = orderModelToParams(action.payload.order)
      const so: StagedOrder = {
        orderType: action.payload.order.type,
        securityId: action.payload.securityId,
        price,
        spread,
        isSpreadOrder,
        size,
        allOrNone: action.payload.order.aon,
        individualMin,
        custId: action.payload.order.selectedBook ?? 0,
        tob,
        totalSize
      }
      return addOrUpdateStagedOrders([so])
    })
  )

const updateStagedOrdersOnCancelOrderEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType('order.cancel', 'order.cancelOrders'),
    mergeMap((action: CancelOrderAction | CancelOrdersAction) => {
      if (!action.payload.updateStagedOrder) {
        return EMPTY
      }

      if (action.type === 'order.cancelOrders') {
        const { orderIds } = action.payload

        const orderToStage = orderIds.reduce((acc, orderId) => {
          const liveOrder = getOrderById(state$.value)(orderId)

          return [
            ...acc,
            {
              securityId: liveOrder!.securityId,
              orderType: liveOrder!.type,
              price: liveOrder!.price,
              spread: liveOrder!.spread,
              isSpreadOrder: liveOrder!.isSpreadOrder,
              size: liveOrder!.size,
              allOrNone: liveOrder!.allOrNone,
              individualMin: liveOrder!.individualMin,
              custId: liveOrder!.custId,
              tob: liveOrder!.tob,
              totalSize: liveOrder!.totalSize
            }
          ]
        }, [])

        return of(addOrUpdateStagedOrders(orderToStage))
      }

      const order = getOrderById(state$.value)(action.payload.orderId)
      const stagedOrder: StagedOrder = {
        securityId: order!.securityId,
        orderType: order!.type,
        price: order!.price,
        spread: order!.spread,
        isSpreadOrder: order!.isSpreadOrder,
        size: order!.size,
        allOrNone: order!.allOrNone,
        individualMin: order!.individualMin,
        custId: order!.custId,
        tob: order!.tob,
        totalSize: order!.totalSize
      }
      return order?.status === 'pending'
        ? of(addOrUpdateStagedOrders([stagedOrder]))
        : EMPTY
    })
  )

const clearCheckedStagedOrders: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType('stagedOrders.clearCheckedStagedOrdersAction'),
    mergeMap(() => {
      const state = state$.value
      const checkedOrders = getCheckedOrders(state)
      /* return checkedOrders.map(checkOrder => {
        return removeStagedOrder(checkOrder)
      })*/
      return of(removeStagedOrders(checkedOrders))
    })
  )

const cancelLiveOrdersOnAddOrUpdateStagedOrders: Epic<Action> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType<AddOrUpdateStagedOrdersAction>(
      'stagedOrders.addOrUpdateStagedOrders'
    ),
    filter((action) => action.payload.cancelLiveOrders),
    mergeMap((action) => {
      const getOrder = getOrderBySecurityAndType(state$.value)
      const orderIds = action.payload.stagedOrders
        .map((stagedOrder) =>
          getOrder(stagedOrder.securityId, stagedOrder.orderType)
        )
        .filter((order) => order?.status === 'pending')
        .map((order) => order!.id)

      return of(cancelOrders(orderIds, true))
    }),
    catchError((err) => of(logError(err)))
  )

const clearFocusedHighlightEpic: Epic = (action$) =>
  action$.pipe(
    ofType<
      | ResetCheckedOrdersAction
      | ApplyTempCheckedOrders
      | SubmitCheckedOrdersAction
      | CancelOrdersAction
    >(
      'checkedOrders.reset',
      'checkedOrders.applyTempCheckedOrders',
      'checkedOrders.submitCheckedOrders',
      'order.cancelOrders'
    ),
    switchMap((action) => {
      return of(setFocusOnOrder(undefined))
    })
  )

export default combineEpics(
  clearOrderValidationOnRemovedStagedOrder,
  clearOrderValidationOnRemovedStagedOrders,
  updateStagedOrdersOnCreateOrderEpic,
  updateStagedOrdersOnCancelOrderEpic,
  clearCheckedStagedOrders,
  cancelLiveOrdersOnAddOrUpdateStagedOrders,
  clearFocusedHighlightEpic
)
